import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, ExternalLink, RotateCcw, Wallet, CheckCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/adminApi";
import { formatApiErrorDetail } from "../../lib/api";
import { formatMoney, formatCents } from "../../lib/pricing";
import { isoToEtLongDate, isoToEtTime, prettyTime } from "../../lib/time";
import StatusBadge from "../../components/admin/StatusBadge";
import ConfirmDialog from "../../components/admin/ConfirmDialog";

const BALANCE_METHODS = [
  { v: "cash", label: "Cash" },
  { v: "card_in_person", label: "Card in person" },
  { v: "bank_transfer", label: "Bank transfer" },
  { v: "other", label: "Other" },
];

export default function AdminBookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [stripeMode, setStripeMode] = useState("test");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [confirmBalance, setConfirmBalance] = useState(false);
  const [balMethod, setBalMethod] = useState("cash");
  const [balNotes, setBalNotes] = useState("");
  const [balSaving, setBalSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get(`/admin/bookings/${id}`)
      .then((r) => { setB(r.data); setNotes(r.data.admin_notes || ""); })
      .catch((e) => toast.error(formatApiErrorDetail(e)));
    adminApi.get("/admin/stripe/status")
      .then((r) => setStripeMode(r.data?.mode || "test"))
      .catch(() => {});
  }, [id]);

  async function saveNotes() {
    setSavingNotes(true);
    try {
      const r = await adminApi.patch(`/admin/bookings/${id}`, { admin_notes: notes });
      setB(r.data);
      toast.success("Notes saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSavingNotes(false);
    }
  }

  async function doCancel() {
    if (!cancelReason.trim()) return toast.error("Please add a cancellation reason.");
    try {
      const r = await adminApi.post(`/admin/bookings/${id}/cancel`, { reason: cancelReason });
      setB(r.data);
      setNotes(r.data.admin_notes || "");
      toast.success("Booking cancelled.");
      setConfirmCancel(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  }

  async function doRefund() {
    if (!refundReason.trim()) return toast.error("A reason is required — deposits are non-refundable by default.");
    setRefunding(true);
    try {
      const r = await adminApi.post(`/admin/bookings/${id}/refund`, { reason: refundReason });
      setB(r.data);
      setNotes(r.data.admin_notes || "");
      toast.success("Deposit refunded via Stripe.");
      setConfirmRefund(false);
      setRefundReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setRefunding(false);
    }
  }

  async function markBalance() {
    setBalSaving(true);
    try {
      const r = await adminApi.post(`/admin/bookings/${id}/balance-collected`, {
        method: balMethod,
        notes: balNotes || null,
      });
      setB(r.data);
      setNotes(r.data.admin_notes || "");
      toast.success("Balance marked as collected.");
      setConfirmBalance(false);
      setBalNotes("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBalSaving(false);
    }
  }

  if (!b) return <div className="text-hbe-ink/50">Loading…</div>;

  const cancelled = b.booking_status === "cancelled";
  const refundable =
    (b.payment_status === "paid" || b.payment_status === "deposit_paid" || b.payment_status === "refund_needed") &&
    !!b.stripe_payment_intent_id;

  const totalCents = Number(b.total_price_cents || Math.round((b.price_total || 0) * 100));
  const depositCents = Number(b.deposit_amount_cents || 0);
  const balanceDue = Number(b.balance_due_cents || 0);
  const balancePaid = Number(b.balance_paid_cents || 0);
  const balanceOutstanding = Math.max(0, balanceDue - balancePaid);
  const balanceCollected = balancePaid > 0 && balanceOutstanding === 0;
  const charterDatePassed = new Date(b.start_datetime).getTime() < Date.now();
  const balanceOverdue = !balanceCollected && charterDatePassed && b.booking_status === "confirmed";

  const dashSlug = stripeMode === "live" ? "" : "test/";
  const stripePiLink = b.stripe_payment_intent_id
    ? `https://dashboard.stripe.com/${dashSlug}payments/${b.stripe_payment_intent_id}`
    : null;
  const stripeSessionLink = b.stripe_checkout_session_id
    ? `https://dashboard.stripe.com/${dashSlug}checkout/sessions/${b.stripe_checkout_session_id}`
    : null;

  return (
    <div data-testid="admin-booking-detail">
      <Link
        to="/admin/bookings"
        className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Back to bookings
      </Link>

      <div className="mt-4">
        <div className="hbe-eyebrow">Reservation</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">{b.confirmation_number}</h1>
        <div className="mt-2 flex flex-wrap gap-2">
          <StatusBadge status={b.booking_status} />
          <StatusBadge status={b.payment_status} />
          {balanceCollected && <StatusBadge status="fully_paid" />}
          {balanceOverdue && <StatusBadge status="balance_overdue" />}
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* Left: reservation info */}
        <div className="space-y-6">
          <div className="hbe-card p-6 space-y-4 text-sm">
            <Row label="Yacht" value={<Link to={`/admin/yachts/${b.yacht_id}`} className="text-hbe-primary hover:underline">{b.yacht_id}</Link>} testid="bd-yacht" />
            <Row label="Date" value={isoToEtLongDate(b.start_datetime)} testid="bd-date" />
            <Row
              label="Time (ET)"
              value={`${prettyTime(isoToEtTime(b.start_datetime))} — ${prettyTime(isoToEtTime(b.end_datetime))}`}
              testid="bd-time"
            />
            <Row label="Duration" value={`${b.duration_hours} hours`} testid="bd-duration" />
            <Row label="Guests" value={String(b.guest_count)} testid="bd-guests" />
            <Row label="Charter total" value={formatMoney(b.price_total, b.currency)} testid="bd-total" />
            {b.special_request && <Row label="Special request" value={b.special_request} testid="bd-request" />}
            {b.occasion && <Row label="Occasion" value={b.occasion} testid="bd-occasion" />}
          </div>

          {/* Balance section */}
          <div className="hbe-card p-6 space-y-4" data-testid="bd-balance-section">
            <div className="flex items-center gap-2">
              <Wallet size={16} className="text-hbe-accent" />
              <h2 className="font-serif text-lg text-hbe-primary">Balance</h2>
            </div>
            <div className="space-y-2 text-sm">
              <Row label="Total charter price" value={formatCents(totalCents)} testid="bd-bal-total" />
              <Row
                label={b.payment_status === "refunded" ? "Deposit (refunded)" : "Deposit paid via Stripe"}
                value={
                  b.payment_status === "refunded"
                    ? `−${formatCents(depositCents)} · refunded`
                    : `${formatCents(depositCents)} ✓`
                }
                testid="bd-bal-deposit"
              />
              <Row
                label={balanceCollected ? "Balance collected" : balanceOverdue ? "Balance OVERDUE" : "Balance due on charter day"}
                value={
                  <span
                    className={balanceCollected ? "text-emerald-700 font-medium" : balanceOverdue ? "text-red-700 font-semibold" : "text-hbe-primary font-medium"}
                    data-testid="bd-bal-outstanding"
                  >
                    {balanceCollected ? `${formatCents(balancePaid)} · ${b.balance_paid_method?.replace("_", " ") || "recorded"}` : formatCents(balanceOutstanding)}
                  </span>
                }
                testid="bd-bal-due"
              />
            </div>
            {!balanceCollected && b.booking_status === "confirmed" && balanceDue > 0 && (
              <button
                type="button"
                onClick={() => setConfirmBalance(true)}
                className="hbe-btn-primary"
                data-testid="bd-mark-balance-collected"
              >
                <CheckCheck size={16} className="mr-2" /> Mark balance as collected
              </button>
            )}
            {balanceCollected && (
              <div className="text-xs text-emerald-700">
                Balance collected {b.balance_paid_at ? `on ${isoToEtLongDate(b.balance_paid_at)}` : ""}.
              </div>
            )}
          </div>

          {(stripePiLink || stripeSessionLink) && (
            <div className="hbe-card p-6 space-y-3 text-sm" data-testid="bd-stripe-block">
              <div className="hbe-eyebrow">Stripe ({stripeMode})</div>
              {stripePiLink && (
                <Row
                  label="Payment intent"
                  testid="bd-stripe-pi"
                  value={
                    <a href={stripePiLink} target="_blank" rel="noreferrer" className="text-hbe-primary hover:underline inline-flex items-center gap-1">
                      {b.stripe_payment_intent_id}
                      <ExternalLink size={12} />
                    </a>
                  }
                />
              )}
              {stripeSessionLink && (
                <Row
                  label="Checkout session"
                  testid="bd-stripe-session"
                  value={
                    <a href={stripeSessionLink} target="_blank" rel="noreferrer" className="text-hbe-primary hover:underline inline-flex items-center gap-1 break-all">
                      {b.stripe_checkout_session_id}
                      <ExternalLink size={12} className="shrink-0" />
                    </a>
                  }
                />
              )}
            </div>
          )}

          <div className="hbe-card p-6">
            <div className="hbe-eyebrow">Admin notes</div>
            <textarea
              rows={4}
              className="hbe-input mt-3 resize-y"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="bd-notes"
            />
            <button
              type="button"
              onClick={saveNotes}
              disabled={savingNotes}
              className="hbe-btn-primary mt-3"
              data-testid="bd-save-notes"
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          </div>
        </div>

        {/* Right: customer + actions */}
        <aside className="space-y-6">
          <div className="hbe-card p-6 space-y-3 text-sm">
            <div className="hbe-eyebrow">Customer</div>
            <Row label="Name" value={b.customer_name} testid="bd-cust-name" />
            <Row label="Email" value={<a className="text-hbe-primary hover:underline" href={`mailto:${b.customer_email}`}>{b.customer_email}</a>} testid="bd-cust-email" />
            <Row label="Phone" value={<a className="text-hbe-primary hover:underline" href={`tel:${b.customer_phone}`}>{b.customer_phone}</a>} testid="bd-cust-phone" />
          </div>

          <div className="hbe-card p-6 space-y-4">
            <div className="hbe-eyebrow">Actions</div>
            <button
              type="button"
              onClick={() => setConfirmCancel(true)}
              disabled={cancelled}
              className="w-full inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium bg-red-700 text-white hover:bg-red-800 min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bd-cancel"
            >
              <Ban size={16} className="mr-2" /> {cancelled ? "Already cancelled" : "Cancel booking"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmRefund(true)}
              disabled={!refundable || b.payment_status === "refunded"}
              className="w-full inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium bg-hbe-primary text-white hover:bg-[#04203a] min-h-[44px] disabled:bg-hbe-soft disabled:text-hbe-ink/50 disabled:cursor-not-allowed"
              title={
                refundable
                  ? "Override the non-refundable policy and refund the deposit via Stripe"
                  : b.payment_status === "refunded"
                  ? "Already refunded"
                  : "No Stripe deposit on this booking"
              }
              data-testid="bd-refund"
            >
              <RotateCcw size={16} className="mr-2" />{" "}
              {b.payment_status === "refunded"
                ? "Deposit refunded"
                : refundable
                ? `Refund ${formatCents(depositCents)} deposit`
                : "Refund (not available)"}
            </button>
            {refundable && b.payment_status !== "refunded" && (
              <div className="text-[11px] text-hbe-ink/60 flex items-start gap-1.5">
                <ShieldAlert size={11} className="mt-0.5 shrink-0" />
                The deposit is <span className="font-semibold">non-refundable</span> per company policy. Refunding overrides this.
              </div>
            )}
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={doCancel}
        title="Cancel this booking?"
        message={
          <>
            <div className="mb-3">
              This releases the slot back to the public availability calendar
              immediately. The deposit is <span className="font-semibold">not automatically refunded</span> —
              use the Refund button separately if you decide to return it.
            </div>
            <label className="hbe-label">Reason (required)</label>
            <input className="hbe-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="bd-cancel-reason" />
          </>
        }
        confirmLabel="Cancel booking"
        danger
      />

      <ConfirmDialog
        open={confirmRefund}
        onClose={() => { if (!refunding) { setConfirmRefund(false); setRefundReason(""); } }}
        onConfirm={doRefund}
        busy={refunding}
        title={`Refund ${formatCents(depositCents)} deposit?`}
        message={
          <>
            <div className="mb-3 flex items-start gap-2 text-red-800 bg-red-50 border border-red-200 rounded-md p-3">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              <span>
                The deposit is <strong>non-refundable per company policy</strong>. Refunding overrides this
                — the customer's booking will be cancelled and the slot released back to the calendar.
                A reason is required for the audit log.
              </span>
            </div>
            <label className="hbe-label">Reason (required)</label>
            <input className="hbe-input" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="e.g. weather, mechanical issue, goodwill" data-testid="bd-refund-reason" />
          </>
        }
        confirmLabel="Refund deposit"
        danger
      />

      <ConfirmDialog
        open={confirmBalance}
        onClose={() => { if (!balSaving) { setConfirmBalance(false); setBalNotes(""); } }}
        onConfirm={markBalance}
        busy={balSaving}
        title={`Mark ${formatCents(balanceOutstanding)} balance as collected?`}
        message={
          <>
            <div className="mb-3">
              Record how the customer paid the remaining balance. This is
              for your records only — nothing is charged to Stripe here.
            </div>
            <label className="hbe-label">Payment method</label>
            <select
              className="hbe-input"
              value={balMethod}
              onChange={(e) => setBalMethod(e.target.value)}
              data-testid="bd-balance-method"
            >
              {BALANCE_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
            <label className="hbe-label mt-3 block">Notes (optional)</label>
            <input
              className="hbe-input"
              value={balNotes}
              onChange={(e) => setBalNotes(e.target.value)}
              placeholder="e.g. cash at marina"
              data-testid="bd-balance-notes"
            />
          </>
        }
        confirmLabel="Mark collected"
      />
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 border-b border-hbe-line/60 pb-3 last:border-0 last:pb-0">
      <div className="text-hbe-ink/55">{label}</div>
      <div className="text-right text-hbe-ink" data-testid={testid}>{value}</div>
    </div>
  );
}
